import { useState, useEffect, useRef, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ZoomIn, ZoomOut, Maximize2, Network, Filter, X, ExternalLink, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

// Node type colors and icons
const NODE_CONFIG = {
  case_missing_person: { color: '#3B82F6', label: 'Missing Person', emoji: '👤' },
  case_unidentified_remains: { color: '#8B5CF6', label: 'Unidentified', emoji: '❓' },
  person_poi: { color: '#EF4444', label: 'Person of Interest', emoji: '🔍' },
  vehicle: { color: '#F59E0B', label: 'Vehicle', emoji: '🚗' },
  location: { color: '#10B981', label: 'Location', emoji: '📍' },
  event: { color: '#6366F1', label: 'Event', emoji: '📅' },
};

// Edge type labels
const EDGE_LABELS = {
  missing_with: 'Missing With',
  family_relationship: 'Family',
  same_suspect: 'Same Suspect',
  same_vehicle: 'Same Vehicle',
  same_location: 'Same Location',
  same_event: 'Same Event',
  reported_possible_link: 'Possible Link',
  investigative_lead: 'Investigative Lead',
  sighting_reported_at: 'Sighting',
  searched_at: 'Searched At',
  found_at: 'Found At',
  last_seen_at: 'Last Seen At',
  potential_match: 'Potential Match',
};

// Confidence colors
const CONFIDENCE_COLORS = {
  confirmed: '#10B981',
  probable: '#F59E0B',
  possible: '#6B7280',
};

export const NetworkGraph = ({ 
  graphData,
  loading = false,
  error = null,
  centerNodeId = null,
  onNodeClick = () => {},
  onRefresh = () => {},
  height = 500,
  showControls = true,
  showLegend = true,
  initialDepth = 1,
  onDepthChange = () => {},
  filters = {},
  onFiltersChange = () => {},
}) => {
  const fgRef = useRef();
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Process graph data for visualization
  const processedData = useCallback(() => {
    if (!graphData?.nodes || !graphData?.edges) {
      return { nodes: [], links: [] };
    }
    
    const nodes = graphData.nodes.map(node => ({
      id: node.id,
      label: node.label,
      type: node.type,
      caseId: node.case_id,
      meta: node.meta,
      color: node.color || NODE_CONFIG[node.type]?.color || '#6B7280',
      size: node.size || (node.id === centerNodeId ? 12 : 8),
    }));
    
    const links = graphData.edges.map(edge => ({
      source: edge.source,
      target: edge.target,
      id: edge.id,
      type: edge.type,
      relationship: edge.relationship,
      confidence: edge.confidence,
      color: edge.color || CONFIDENCE_COLORS[edge.confidence] || '#6B7280',
      width: edge.width || (edge.confidence === 'confirmed' ? 3 : edge.confidence === 'probable' ? 2 : 1),
    }));
    
    return { nodes, links };
  }, [graphData, centerNodeId]);
  
  const data = processedData();
  
  // Node paint function
  const paintNode = useCallback((node, ctx, globalScale) => {
    const label = node.label || 'Unknown';
    const fontSize = 12 / globalScale;
    const nodeSize = node.size || 8;
    const isHovered = hoveredNode?.id === node.id;
    const isSelected = selectedNode?.id === node.id;
    const isCenter = node.id === centerNodeId;
    
    // Draw node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize + (isHovered ? 2 : 0), 0, 2 * Math.PI);
    ctx.fillStyle = node.color;
    ctx.fill();
    
    // Draw border for center/selected nodes
    if (isCenter || isSelected) {
      ctx.strokeStyle = isCenter ? '#ffffff' : '#000000';
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }
    
    // Draw label
    if (globalScale > 0.5 || isHovered || isSelected) {
      const truncatedLabel = label.length > 20 ? label.substring(0, 18) + '...' : label;
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#374151';
      ctx.fillText(truncatedLabel, node.x, node.y + nodeSize + 3);
    }
  }, [hoveredNode, selectedNode, centerNodeId]);
  
  // Link label function
  const linkLabel = useCallback((link) => {
    return `${EDGE_LABELS[link.type] || link.type} (${link.confidence})`;
  }, []);
  
  // Handle node hover
  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node);
    document.body.style.cursor = node ? 'pointer' : 'default';
  }, []);
  
  // Handle node click
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    onNodeClick(node);
    
    // Center on clicked node
    if (fgRef.current && node) {
      fgRef.current.centerAt(node.x, node.y, 500);
      fgRef.current.zoom(2, 500);
    }
  }, [onNodeClick]);
  
  // Zoom controls
  const handleZoomIn = () => fgRef.current?.zoom(fgRef.current.zoom() * 1.5, 300);
  const handleZoomOut = () => fgRef.current?.zoom(fgRef.current.zoom() / 1.5, 300);
  const handleFitView = () => fgRef.current?.zoomToFit(400);
  
  // Center on initial node
  useEffect(() => {
    if (fgRef.current && centerNodeId && data.nodes.length > 0) {
      setTimeout(() => {
        fgRef.current?.zoomToFit(400, 50);
      }, 500);
    }
  }, [centerNodeId, data.nodes.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-50 dark:bg-slate-900 rounded-lg">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-muted-foreground">Loading network...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-slate-50 dark:bg-slate-900 rounded-lg">
        <Info className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onRefresh}>
          Try Again
        </Button>
      </div>
    );
  }
  
  if (data.nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-slate-50 dark:bg-slate-900 rounded-lg border-2 border-dashed">
        <Network className="w-12 h-12 text-muted-foreground/50 mb-3" />
        <p className="text-muted-foreground text-center">
          No network connections found.<br />
          <span className="text-sm">Add connections to build the case network.</span>
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Controls */}
      {showControls && (
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <Button variant="outline" size="icon" onClick={handleZoomIn} className="bg-white/90 dark:bg-slate-900/90">
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={handleZoomOut} className="bg-white/90 dark:bg-slate-900/90">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={handleFitView} className="bg-white/90 dark:bg-slate-900/90">
            <Maximize2 className="w-4 h-4" />
          </Button>
          <Button 
            variant={showFilters ? "default" : "outline"} 
            size="icon" 
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "" : "bg-white/90 dark:bg-slate-900/90"}
          >
            <Filter className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="absolute top-14 right-3 z-10 bg-white dark:bg-slate-900 rounded-lg shadow-lg p-4 w-64 border">
          <div className="flex justify-between items-center mb-3">
            <span className="font-medium text-sm">Filters</span>
            <Button variant="ghost" size="icon" onClick={() => setShowFilters(false)} className="h-6 w-6">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Depth</label>
              <Select value={String(filters.depth || 1)} onValueChange={(v) => onFiltersChange({ ...filters, depth: parseInt(v) })}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hop</SelectItem>
                  <SelectItem value="2">2 hops</SelectItem>
                  <SelectItem value="3">3 hops</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Show Entity Types</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={filters.include_vehicles} 
                    onCheckedChange={(v) => onFiltersChange({ ...filters, include_vehicles: v })}
                  />
                  Vehicles
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={filters.include_locations} 
                    onCheckedChange={(v) => onFiltersChange({ ...filters, include_locations: v })}
                  />
                  Locations
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={filters.include_events} 
                    onCheckedChange={(v) => onFiltersChange({ ...filters, include_events: v })}
                  />
                  Events
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Confidence</label>
              <Select value={filters.confidence_filter || 'all'} onValueChange={(v) => onFiltersChange({ ...filters, confidence_filter: v === 'all' ? null : v })}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="confirmed">Confirmed only</SelectItem>
                  <SelectItem value="probable">Probable+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
      
      {/* Graph */}
      <div className="border rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900" style={{ height }}>
        <ForceGraph2D
          ref={fgRef}
          graphData={data}
          nodeCanvasObject={paintNode}
          nodePointerAreaPaint={(node, color, ctx) => {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, node.size || 8, 0, 2 * Math.PI);
            ctx.fill();
          }}
          linkColor={(link) => link.color}
          linkWidth={(link) => link.width}
          linkDirectionalParticles={2}
          linkDirectionalParticleSpeed={0.005}
          linkLabel={linkLabel}
          onNodeHover={handleNodeHover}
          onNodeClick={handleNodeClick}
          cooldownTicks={100}
          onEngineStop={() => fgRef.current?.zoomToFit(400, 50)}
          enableNodeDrag={true}
          enableZoomInteraction={true}
          enablePanInteraction={true}
        />
      </div>
      
      {/* Legend */}
      {showLegend && (
        <div className="mt-3 flex flex-wrap gap-3">
          {Object.entries(NODE_CONFIG).map(([type, config]) => (
            <div key={type} className="flex items-center gap-1.5 text-xs">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
          ))}
          <div className="border-l pl-3 ml-1 flex gap-3">
            {Object.entries(CONFIDENCE_COLORS).map(([conf, color]) => (
              <div key={conf} className="flex items-center gap-1.5 text-xs">
                <div className="w-4 h-0.5" style={{ backgroundColor: color }} />
                <span className="text-muted-foreground capitalize">{conf}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Selected Node Info */}
      {selectedNode && (
        <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-lg border shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <Badge 
                variant="outline" 
                className="mb-2"
                style={{ borderColor: NODE_CONFIG[selectedNode.type]?.color, color: NODE_CONFIG[selectedNode.type]?.color }}
              >
                {NODE_CONFIG[selectedNode.type]?.label || selectedNode.type}
              </Badge>
              <h4 className="font-medium">{selectedNode.label}</h4>
              {selectedNode.meta?.description && (
                <p className="text-sm text-muted-foreground mt-1">{selectedNode.meta.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              {selectedNode.caseId && (
                <Link to={`/case/${selectedNode.caseId}`}>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View Case
                  </Button>
                </Link>
              )}
              <Button variant="ghost" size="icon" onClick={() => setSelectedNode(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats */}
      {graphData?.stats && (
        <div className="mt-3 text-xs text-muted-foreground">
          {graphData.stats.total_nodes} nodes • {graphData.stats.total_edges} connections
        </div>
      )}
    </div>
  );
};

export default NetworkGraph;
